import { Box, Checkbox, List, ListItem, ListItemAvatar, ListItemButton, ListItemText } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setAutocolonItem, setAutocolonSelectItem, setGroupSelectedItem } from 'app/actions/transport';
import { Autosizer, Scrollbar } from 'app/components';
import { TransportCircleIcon, TransportSquareIcon, TransportTriangleIcon } from 'app/icons';
import React, { forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const TransportGroupList = ({ setHaveUpdate }) => {
  const dispatch = useDispatch();
  const { groupItems, groupSearchValue, groupSelectedItem, autocolonSelectedItem } = useSelector((state) => state.transport);
  const sortItems = useMemo(() => {
    const compareFn = (a, b) => a.name.localeCompare(b.name, { sensitivity: 'base' });

    if (groupSearchValue.length) {
      return groupItems.filter((item) => item.name.toLowerCase().indexOf(groupSearchValue.toLowerCase()) !== -1).sort(compareFn);
    }

    return groupItems.sort(compareFn);
  }, [groupItems, groupSearchValue]);

  const handleItemClick = (value) => {
    if (groupSelectedItem?.id === value.id) {
      dispatch(setGroupSelectedItem(null));
    } else {
      dispatch(setGroupSelectedItem(value));
    }
  };

  const handleCheckGroup = (e, group) => {
    e.preventDefault();
    e.stopPropagation();
    if (autocolonSelectedItem.autocolons) return;
    setHaveUpdate(true);
    let groupItems = autocolonSelectedItem?.groupItems || [];
    if (groupItems.includes(group.id)) {
      groupItems = groupItems.filter((item) => item !== group.id);
    } else {
      groupItems.push(group.id);
    }
    const newAutocolonItem = { ...autocolonSelectedItem, groupItems };
    dispatch(setAutocolonItem(newAutocolonItem));
    dispatch(
      setAutocolonSelectItem({
        ...autocolonSelectedItem,
        groupItems,
      })
    );
  };
  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
          '&:hover': {
            bgcolor: '#f5f6fa',
          },
          '.MuiCheckbox-root': {
            '&:hover': {
              bgcolor: 'transparent',
            },
          },
          '.MuiListItemAvatar-root': {
            minWidth: '36px',
            maxHeight: '24px',
          },
          '.MuiAvatar-circular': {
            width: '24px',
            height: '24px',
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            svg: {
              fontSize: '16px',
              path: {
                fill: theme.palette.primary.main,
              },
            },
          },
          '.MuiListItemButton-root': {
            padding: theme.spacing(1, 0),
            borderRadius: '0',
            maxHeight: '100%',
          },
          '.MuiListItemText-root': {
            margin: theme.spacing(0),
          },
          '&.Mui-selected .MuiButtonBase-root.MuiCheckbox-root': {
            color: theme.palette.common.white,
          },
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <Scrollbar>
              {({ scrollableNodeRef, contentNodeRef }) => (
                <FixedSizeList
                  height={height}
                  itemSize={36}
                  overscanCount={10}
                  itemCount={sortItems.length}
                  innerRef={contentNodeRef}
                  outerRef={scrollableNodeRef}
                  innerElementType={transportGroupListInnerElementType}
                  itemData={{
                    items: sortItems,
                    selectedItem: groupSelectedItem,
                    onClick: handleItemClick,
                    autocolonSelectedItem,
                    handleCheckGroup,
                  }}>
                  {transportGroupListItemRender}
                </FixedSizeList>
              )}
            </Scrollbar>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const transportGroupListInnerElementType = forwardRef((props, ref) => <List ref={ref} disablePadding {...props} />);

const transportGroupListItemRender = ({ data, index, style }) => {
  const { items, selectedItem, onClick, autocolonSelectedItem, handleCheckGroup } = data;
  const checked = (autocolonSelectedItem?.groupItems || []).includes(items[index].id);
  return (
    <ListItem key={items[index].id} style={style} selected={items[index].id === selectedItem?.id}>
      <ListItemButton onClick={() => onClick(items[index])}>
        <Checkbox checked={checked} color='primary' disableRipple onClick={(e) => handleCheckGroup(e, items[index])} />
        <ListItemAvatar>
          {
            {
              circle: <TransportCircleIcon color={items[index].color} />,
              triangle: <TransportTriangleIcon color={items[index].color} />,
              square: <TransportSquareIcon color={items[index].color} />,
              null: <TransportCircleIcon sx={{ opacity: 0 }} />,
            }[items[index].type]
          }
        </ListItemAvatar>
        <ListItemText primary={items[index].name} />
      </ListItemButton>
    </ListItem>
  );
};

export default TransportGroupList;
